import React, { FC, Fragment, memo, useEffect, useState } from 'react';
import { possibilities } from './paymentAiTarif';
import IconSvgPayment from '../IconSvgPayment';
import { useTranslation } from 'react-i18next';
import ListItem from '../../ui/ListItem';
import Checkbox from '../../ui/Checkbox';
import Button from '../../ui/Button';
import ConfirmPurchaseAi from './ConfirmPurchaseAi';
import useFlag from '../../../hooks/useFlag';
import classNames from 'classnames';
import useEffectOnce from '../../../hooks/useEffectOnce';
import ConfirmPayModal from '../ConfirmPayModal';
import {
  AiPrompt,
  GlobalState,
  IPayment,
  TabState,
} from '../../../global/types';
import { getActions, withGlobal } from '../../../global';
import { selectTabState } from '../../../global/selectors';
import useLastCallback from '../../../hooks/useLastCallback';
import { formatDateToString } from '../../../util/dateFormat';

type OwnProps = {
  prompt: AiPrompt;
  payment?: IPayment;
};

type StateProps = Pick<GlobalState, 'aiPurchases'> &
  Pick<TabState, 'middleScreen'>;

const AiPackPage: FC<OwnProps & StateProps> = ({
  prompt,
  payment,
  aiPurchases,
}) => {
  const { t } = useTranslation();
  const { apiUpdate } = getActions();
  const { name, count, price, unlim } = prompt;
  const [isOpenConfirm, openConfirm, closeConfirm] = useFlag();
  const [typePurchase, setTypePurchase] = useState('pack');
  const [visibleDescription, setVisibleDescription] = useState('text');
  const [activeTitle, setActiveTitle] = useState<string>('');
  const [activeExpired, setActiveExpired] = useState<number>();

  const {
    double_sub_expired,
    double_expiration_date,
    img_sub_expired,
    img_expiration_date,
    text_sub_expired,
    text_expiration_date,
  } = aiPurchases || {};

  const { status } = payment || {};

  useEffect(() => {
    let title;
    switch (name) {
      case 'double':
        title = !double_sub_expired
          ? 'PaymentAi.double.Title'
          : !text_sub_expired
          ? 'PaymentAi.text.Title'
          : !img_sub_expired
          ? 'PaymentAi.img.Title'
          : '';
        setActiveExpired(
          !double_sub_expired
            ? double_expiration_date
            : !text_sub_expired
            ? text_expiration_date
            : !img_sub_expired
            ? img_expiration_date
            : 0
        );
        break;
      case 'text':
        title = !text_sub_expired
          ? 'PaymentAi.text.Title'
          : !double_sub_expired
          ? 'PaymentAi.double.Title'
          : '';
        setActiveExpired(
          !text_sub_expired
            ? text_expiration_date
            : !double_sub_expired
            ? double_expiration_date
            : 0
        );
        break;
      case 'img':
        title = !img_sub_expired
          ? 'PaymentAi.img.Title'
          : !double_sub_expired
          ? 'PaymentAi.double.Title'
          : '';
        setActiveExpired(
          !img_sub_expired
            ? img_expiration_date
            : !double_sub_expired
            ? double_expiration_date
            : 0
        );
    }
    setActiveTitle(t(title));
  }, [name]);

  const handleClearPayment = useLastCallback(() => {
    apiUpdate({
      '@type': 'updatePaymentState',
      payment: undefined,
    });
  });

  useEffectOnce(() => {
    if (name === 'img') {
      setVisibleDescription('img');
    }
  });

  return (
    <div className='payment-content custom-scroll'>
      <div className={`payment-wrap ai-pack-wrap ai-pack-${name}`}>
        <IconSvgPayment name={`${name}-round`} />
        <div className='text-center'>
          <h2>{t(`PaymentAi.${name}.TitlePlans`)}</h2>
          <div className='description'>Run out of prompts?</div>
        </div>
        <div className='check-block'>
          <ListItem
            className='check-unlim'
            rightElement={<IconSvgPayment name='flash-round' />}
            onClick={() => setTypePurchase('subscription')}
            ripple
          >
            <Checkbox
              label=''
              square
              checked={typePurchase === 'subscription'}
            />
            <div className='info'>
              <div className='title text-bold'>Unlimited Monthly</div>
              <div className='price text-muted'>
                <span>${unlim}</span>/month
              </div>
            </div>
          </ListItem>
          <ListItem onClick={() => setTypePurchase('pack')} ripple>
            <Checkbox label='' square checked={typePurchase === 'pack'} />
            <div className='info'>
              <div className='title'>Individual Prompts</div>
              <div className='price'>
                {Array.isArray(count) ? (
                  count.map((qty, idx) => (
                    <Fragment key={idx}>
                      {idx === 0 ? 'Chats' : 'Images'}{' '}
                      <span className='count'>{qty}</span> {idx === 0 && '/ '}
                    </Fragment>
                  ))
                ) : (
                  <span className='count'>{count}</span>
                )}
                <>
                  {!Array.isArray(count) && ' for '}
                  <span> ${price}</span>
                </>
              </div>
            </div>
          </ListItem>
        </div>
        {typePurchase === 'subscription' && activeExpired && activeTitle ? (
          <div className='pack-active'>
            <b>{activeTitle} Pack Active</b>
            <small>
              Expires until {formatDateToString(activeExpired * 1000)}
            </small>
          </div>
        ) : (
          <Button color='success' fullWidth onClick={openConfirm}>
            Pay ${typePurchase === 'pack' ? price : unlim}
          </Button>
        )}

        {possibilities && (
          <div className='main-description'>
            {name === 'double' && (
              <div className='btn-group tab-nav full-width'>
                <div
                  className={classNames('Button text primary', {
                    active: visibleDescription === 'text',
                  })}
                  onClick={() => setVisibleDescription('text')}
                >
                  AI Chat
                </div>
                <div
                  className={classNames('Button text primary', {
                    active: visibleDescription === 'img',
                  })}
                  onClick={() => setVisibleDescription('img')}
                >
                  AI Image
                </div>
              </div>
            )}
            <ul>
              {possibilities[visibleDescription].map((row) => (
                <li key={row}>
                  <div className='content'>
                    <div>{t(`PaymentAi.${visibleDescription}.${row}`)}:</div>
                    <span>
                      {t(`PaymentAi.${visibleDescription}.${row}Description`)}{' '}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <ConfirmPurchaseAi
        type={typePurchase}
        namePurchase={name}
        isOpen={isOpenConfirm}
        onClose={closeConfirm}
        purchaseData={prompt}
      />
      <ConfirmPayModal
        isOpen={
          !isOpenConfirm &&
          Boolean(status) &&
          ['completed', 'canceled'].includes(status)
        }
        title='Success!'
        closeLabel='Back'
        header='Congratulations!'
        onClose={handleClearPayment}
        text={
          typePurchase === 'pack'
            ? name === 'double'
              ? t('PaymentAi.YouHavePurchasedDuble', { returnObjects: true })
              : t('PaymentAi.YouHavePurchased', {
                  count: typeof count === 'number' ? count : 0,
                  returnObjects: true,
                })
            : t('PaymentAi.YouHavePurchasedUnlim', {
                returnObjects: true,
                prompt:
                  name === 'text'
                    ? 'chat prompts'
                    : name === 'img'
                    ? 'image prompts'
                    : 'images and chats prompts',
              })
        }
        payment={payment}
        transactionType='aiPurchased'
      />
    </div>
  );
};

export default memo(
  withGlobal((global): StateProps => {
    const { aiPurchases } = global;
    const tabState = selectTabState(global);
    return { middleScreen: tabState.middleScreen, aiPurchases };
  })(AiPackPage)
);
